export const rendererKinds = {
  "video-grid": "视频网格",
  "content-list": "内容列表",
  "ad-slot": "广告位",
  navigation: "自定义导航",
  banner: "图片横幅",
  text: "文字",
  image: "图片",
  "page-link": "页面入口",
  builtin: "系统业务区块",
};
export const gridSlots = [
  "home:catalog",
  "discover:videos",
  "profile:videos",
  "search:results",
  "collection:content",
];
export const rendererFields = {
  title: ["title", "name"],
  image: ["portrait", "landscape"],
  description: ["description", "actors", "category", "year", "area"],
  plays: ["views", "playCount"],
};
const defaultFields = {
  title: "title",
  image: "portrait",
  description: "description",
  plays: "views",
};
export function normalizeRenderers(value) {
  const rows = Array.isArray(value)
    ? value
    : Object.entries(rendererKinds).map(([kind, name]) => ({ id: `R-${kind}`, name, kind }));
  return rows.slice(0, 100).map((r, i) => ({
    id: String(r?.id || `R-${i + 1}`).slice(0, 160),
    name: String(r?.name || "新渲染器")
      .trim()
      .slice(0, 80),
    kind: Object.hasOwn(rendererKinds, r?.kind) ? r.kind : "content-list",
    status: r?.status === "停用" ? "停用" : "启用",
    css: String(r?.css || "").slice(0, 8000),
    fields: Object.fromEntries(
      Object.entries(rendererFields).map(([key]) => [
        key,
        String(r?.fields?.[key] || defaultFields[key]).slice(0, 80),
      ]),
    ),
    defaults: {
      columns: Math.min(8, Math.max(1, Number(r?.defaults?.columns) || 2)),
      gap: Math.min(48, Math.max(0, Number(r?.defaults?.gap) || 0)),
      showTitle: r?.defaults?.showTitle !== false,
      showPlays: r?.defaults?.showPlays === true,
    },
  }));
}
export function getRendererReferences(config, id) {
  return config.components
    .filter((c) => (c.rendererId || `R-${c.type}`) === id)
    .map((c) => `组件“${c.name}”`);
}
// Deliberately small CSS grammar: scoped parts + presentation declarations.
// No scripts, network resources, global selectors, positioning, or arbitrary data queries.
const parts = {
  ":host": "",
  ".items": ".component-data-items",
  ".card": ".component-data-card",
  ".image": ".component-image",
  ".title": ".component-title",
  ".description": ".component-description",
  ".plays": ".component-plays",
};
const properties = new Set(
  "color background-color font-size font-weight line-height letter-spacing text-align padding padding-top padding-bottom padding-left padding-right margin margin-top margin-bottom margin-left margin-right gap row-gap column-gap border border-color border-width border-style border-radius opacity box-shadow object-fit aspect-ratio".split(
    " ",
  ),
);
export function cssError(css) {
  if (!css.trim()) return "";
  const clean = css.replace(/\/\*[\s\S]*?\*\//g, "");
  if (/[<>@\\]/.test(clean) || /url\s*\(|expression\s*\(|!\s*important|var\s*\(/i.test(clean))
    return "CSS 只能设置组件局部的展示样式，不支持资源地址、全局规则或脚本";
  const rules = [...clean.matchAll(/([^{}]+)\{([^{}]*)\}/g)];
  if (!rules.length || clean.replace(/[^{}]+\{[^{}]*\}/g, "").trim()) return "CSS 格式不正确";
  for (const [, selectors, body] of rules) {
    if (selectors.split(",").some((s) => !Object.hasOwn(parts, s.trim())))
      return "CSS 选择器仅支持 :host、.items、.card、.image、.title、.description、.plays";
    for (const declaration of body.split(";").filter((s) => s.trim())) {
      const match = declaration.trim().match(/^([a-z-]+)\s*:\s*([a-zA-Z0-9#.,%() +\/-]+)$/);
      if (!match || !properties.has(match[1])) return "CSS 包含不支持的属性或属性值";
    }
  }
  return "";
}
export function scopedRendererCss(css, scope) {
  if (cssError(css) || !/^r-[a-z0-9-]+$/.test(scope)) return "";
  return css.replace(/\/\*[\s\S]*?\*\//g, "").replace(
    /([^{}]+)\{([^{}]*)\}/g,
    (_, sels, body) =>
      `${sels
        .split(",")
        .map(
          (s) => `[data-renderer-scope="${scope}"]${parts[s.trim()] ? " " + parts[s.trim()] : ""}`,
        )
        .join(",")}{${body}}`,
  );
}
export function rendererScope(id) {
  // Encode every code point, so IDs cannot escape a selector and do not collide.
  return "r-" + Array.from(id, (c) => c.codePointAt(0).toString(16)).join("-");
}
export function boundVideoFields(video, fields) {
  const f = { ...defaultFields, ...fields };
  const read = (key) => (rendererFields[key].includes(f[key]) ? video[f[key]] : undefined);
  return {
    title: read("title") || video.title || video.name || "未命名视频",
    image: read("image") || video.portrait || video.landscape || "",
    description: read("description") || "",
    plays: read("plays") ?? "—",
  };
}
